import { useState, useEffect, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import type { UiPhoto } from '@/data/drops'

type Props = {
  photos: UiPhoto[]
  initialIndex: number
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function PhotoLightbox({ photos, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex)
  const photo = photos[index]

  const prev = useCallback(() => setIndex(i => (i > 0 ? i - 1 : photos.length - 1)), [photos.length])
  const next = useCallback(() => setIndex(i => (i < photos.length - 1 ? i + 1 : 0)), [photos.length])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, prev, next])

  if (!photo) return null

  const filename = photo.originalUrl.split('/').pop() || 'photo'
  const compressionRatio = photo.processedSizeBytes && photo.fileSizeBytes
    ? ((1 - photo.processedSizeBytes / photo.fileSizeBytes) * 100).toFixed(0)
    : null

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex" onClick={onClose}>
      {/* Main image area */}
      <div className="flex-1 flex items-center justify-center relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10">
          <X className="h-6 w-6" />
        </button>
        {photos.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-4 text-white/70 hover:text-white">
              <ChevronLeft className="h-8 w-8" />
            </button>
            <button onClick={next} className="absolute right-4 text-white/70 hover:text-white">
              <ChevronRight className="h-8 w-8" />
            </button>
          </>
        )}
        <img
          src={photo.originalUrl}
          alt={photo.caption || filename}
          className="max-h-[90vh] max-w-[70vw] object-contain"
        />
      </div>

      {/* Metadata panel */}
      <div className="w-72 bg-card border-l p-5 overflow-y-auto text-sm" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold mb-4 truncate">{filename}</h3>

        <div className="space-y-3">
          <div>
            <p className="text-muted-foreground text-xs">Uploaded</p>
            <p>{new Date(photo.uploadedAt).toLocaleString()}</p>
          </div>

          {photo.width && photo.height && (
            <div>
              <p className="text-muted-foreground text-xs">Dimensions</p>
              <p>{photo.width} x {photo.height}</p>
            </div>
          )}

          <div>
            <p className="text-muted-foreground text-xs">Original Size</p>
            <p>{formatBytes(photo.fileSizeBytes)}</p>
          </div>

          {photo.processedSizeBytes && (
            <div>
              <p className="text-muted-foreground text-xs">Processed Size</p>
              <p>{formatBytes(photo.processedSizeBytes)}{compressionRatio && ` (${compressionRatio}% smaller)`}</p>
            </div>
          )}

          <div>
            <p className="text-muted-foreground text-xs">Status</p>
            <p className="capitalize">{photo.processingStatus}</p>
          </div>

          {photo.caption && (
            <div>
              <p className="text-muted-foreground text-xs">Caption</p>
              <p>{photo.caption}</p>
            </div>
          )}

          <a
            href={photo.originalUrl}
            download={filename}
            className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border hover:bg-accent w-full justify-center mt-4"
          >
            <Download className="h-3 w-3" /> Download original
          </a>
        </div>
      </div>
    </div>
  )
}
